import { ref } from 'vue'
import { auth } from '@/firebase'
import { apiPost } from '@/composables/useApi'
import { createListeningTracker } from './listeningTracker'

const storageKey = 'goodscriber-listening-v2'
// 某次上报刚好完成当天打卡时，后端带回的奖励明细；App 监听它弹出提示
export const checkInReward = ref(null)
const tracker = createListeningTracker({
  getUserId: () => auth.currentUser?.uid,
  now: () => performance.now(),
  wallNow: () => Date.now(),
  newId: () => crypto.randomUUID(),
  send: async body => {
    const result = await apiPost('/api/users/listening-stats', body, { keepalive: true })
    if (result.checkInReward) checkInReward.value = result.checkInReward
    return result
  },
  load: () => {
    try { return JSON.parse(sessionStorage.getItem(storageKey)) } catch { return null }
  },
  save: state => {
    try { sessionStorage.setItem(storageKey, JSON.stringify(state)) } catch { /* Storage may be disabled. */ }
  },
})

export function flushListeningActivity() {
  return tracker.flush().catch(error => console.warn('Listening activity upload pending:', error.message))
}

setInterval(() => tracker.tick(), 1000)
setInterval(flushListeningActivity, 30000)
window.addEventListener('pagehide', flushListeningActivity)
document.addEventListener('visibilitychange', () => {
  if (document.hidden) flushListeningActivity()
})

export function trackMediaListening(media) {
  const read = () => ({ position: Number(media.currentTime) || 0, rate: Number(media.playbackRate) || 1 })
  const start = () => tracker.begin(media, read)
  const stop = () => { tracker.stop(media); flushListeningActivity() }
  const seek = () => tracker.stop(media)
  const resume = () => { if (!media.paused && !media.ended) start() }
  const handlers = { play: start, playing: start, pause: stop, ended: stop, waiting: stop, error: stop, emptied: stop, seeking: seek, seeked: resume, ratechange: () => { seek(); resume() } }
  for (const [event, handler] of Object.entries(handlers)) media.addEventListener(event, handler)
  return () => {
    stop()
    for (const [event, handler] of Object.entries(handlers)) media.removeEventListener(event, handler)
  }
}

export function trackSpeechListening(utterance) {
  let started = 0
  let position = 0
  const read = () => ({ position: position + (performance.now() - started) / 1000, rate: 1 })
  const start = () => { started = performance.now(); tracker.begin(utterance, read) }
  const stop = () => {
    tracker.stop(utterance)
    position = read().position
    flushListeningActivity()
  }
  utterance.addEventListener('start', start)
  utterance.addEventListener('resume', start)
  for (const event of ['pause', 'end', 'error']) utterance.addEventListener(event, stop)
}

export function trackHowlListening(howl) {
  let soundId
  const read = () => ({ position: Number(howl.seek(undefined, soundId)) || 0, rate: Number(howl.rate()) || 1 })
  const start = id => { soundId = id; tracker.begin(howl, read) }
  const stop = () => { tracker.stop(howl); flushListeningActivity() }
  const seek = () => { tracker.stop(howl); if (howl.playing(soundId)) start(soundId) }
  const handlers = { play: start, pause: stop, stop, end: stop, loaderror: stop, playerror: stop, seek, rate: seek }
  for (const [event, handler] of Object.entries(handlers)) howl.on(event, handler)
  // Howler resets the timeline before delivering stop/end callbacks. Capture
  // the final fraction of a second before its public methods change position.
  const originals = {}
  for (const method of ['stop', 'pause', 'unload', 'seek', 'rate']) {
    originals[method] = howl[method]
    howl[method] = function (...args) {
      const changing = !['seek', 'rate'].includes(method) || typeof args[0] === 'number'
      if (changing) tracker.stop(howl)
      const result = originals[method].apply(this, args)
      if (changing) {
        if (['seek', 'rate'].includes(method) && howl.playing(soundId)) start(soundId)
        flushListeningActivity()
      }
      return result
    }
  }
  return () => {
    stop()
    for (const [method, original] of Object.entries(originals)) howl[method] = original
    for (const [event, handler] of Object.entries(handlers)) howl.off(event, handler)
  }
}
